<script setup lang="ts">
import type { Group } from '@/types/group';
import type { Follower } from '@/types/profile';

const route = useRoute();
const router = useRouter();
const profileStore = useProfileStore();
const userStore = useUserStore();
const groupStore = useGroupStore();
const authStore = useAuthStore();
const notificationStore = useNotificationStore();
const { trackEvent } = useGtm();
const { setDocumentTitle } = useSetDocumentTitle();
const { isMobile } = useWindowResize();

const isOpenSidebar = ref(false);
const isShowMessageDialog = ref(false);
const isShowNoFollowerDialog = ref(false);
const isShowGroupEditDialog = ref(false);
const followerNumber = ref();
const followBtnRef = ref();
const followers = ref<Follower[]>([]);
const currentFollowerListPage = ref(1);
const isOwner = ref(false);
const isShowGoogleLoginFailDialog = ref(false);

const group = reactive<Partial<Group>>({
	groupName: '',
	groupImageUrl: '',
	groupDescription: '',
	groupCoverImageUrl: '',
});

const profileId = computed(() => Number(profileStore.profileId));
const organizer = computed(() => groupStore.groupInfo?.owner);
const organizerId = computed(() => Number(groupStore.groupInfo?.owner.id));
const isLoading = computed(() => profileStore.isLoading || groupStore.isLoading || userStore.isLoading);

const userRole = computed(() => {
	if (userStore.role.isNotMember) {
		return null;
	}
	return userStore.userRole;
});

const groupId = computed(() => {
	if (route.name === ROUTE.GROUP.NAME) {
		return Number(route.params?.id);
	}
	return null;
});

const sharedGroupUrl = computed(() => {
	return getFullUrlPath(`/group/${groupId.value}`);
});

function handleSideDrawerClose() {
	isOpenSidebar.value = false;
}

function handleOnDialogClose() {
	isShowMessageDialog.value = false;
}

function handleShowMessageDialog() {
	const follower = groupStore.groupInfo?.stats?.followers;
	if (follower) {
		isShowMessageDialog.value = true;
	} else {
		isShowNoFollowerDialog.value = true;
	}
}

async function handleOnSendMessage(subject: string, body: string) {
	if (!groupId.value) {
		isShowMessageDialog.value = false;
		return;
	}

	await groupStore.sendMessageToGroupFollowers(
		groupId.value,
		{
			body,
			subject,
		},
	);

	if (groupStore.errorMessage) {
		notificationStore.showErrorNotification(groupStore.errorMessage);
	} else {
		notificationStore.showSuccessNotification('Successfully sent the message.');
		trackEvent({
			user_id: profileId.value,
			group_id: groupId.value,
			message_sending_method: GTM_MESSAGE_SENDING_METHOD_PARAMS.GROUP_FOLLOWERS,
			event: GTM_EVENT_NAME.MESSAGE_SENT,
		});
	}

	isShowMessageDialog.value = false;
}

async function handleUpdateGroup(data: Group) {
	if (!groupId.value) {
		notificationStore.showErrorNotification(profileStore.errorMessage);
		isShowGroupEditDialog.value = false;
		return;
	}

	const { groupCoverImageFile, groupImageFile, isDeletedLogoImage, isDeletedCoverImage, ...newData } = data;
	const hasDeletedImage = isDeletedLogoImage || isDeletedCoverImage;

	// Check for group name or description changes
	if (
		newData.groupDescription !== group.groupDescription ||
		newData.groupName !== group.groupName
	) {
		await groupStore.updateGroupInfo(groupId.value, {
			// description: newData.groupDescription || ' ',
			...((newData.groupName) ? { name: newData.groupName } : {}),
			...((newData.groupDescription || newData.groupDescription === '') ? { description: newData.groupDescription || ' ' } : {}),
		});
	}

	// Check for group cover and logo image changes
	if (groupCoverImageFile && groupImageFile && !hasDeletedImage) {
		// Upload both cover image and logo image
		await groupStore.uploadGroupImage(groupId.value, {
			logo: groupImageFile,
			cover: groupCoverImageFile,
		}, [GROUP_IMAGE_FIELD.LOGO, GROUP_IMAGE_FIELD.COVER_IMAGE]);
	} else if (groupCoverImageFile && !hasDeletedImage) {
		// Upload only cover image
		await groupStore.uploadGroupImage(groupId.value, { cover: groupCoverImageFile }, [GROUP_IMAGE_FIELD.COVER_IMAGE]);
	} else if (groupImageFile && !hasDeletedImage) {
		// Upload only logo image
		await groupStore.uploadGroupImage(groupId.value, { logo: groupImageFile }, [GROUP_IMAGE_FIELD.LOGO]);
	} else if (hasDeletedImage && (isDeletedLogoImage && isDeletedCoverImage)) {
		// Delete both logo and cover image
		await Promise.all([
			groupStore.deleteGroupImage(groupId.value, GROUP_IMAGE_FIELD.LOGO),
			groupStore.deleteGroupImage(groupId.value, GROUP_IMAGE_FIELD.COVER_IMAGE),
		]);
	} else if (hasDeletedImage && isDeletedLogoImage) {
		// Delete a logo image
		await groupStore.deleteGroupImage(groupId.value, GROUP_IMAGE_FIELD.LOGO);
	} else if (hasDeletedImage && isDeletedCoverImage) {
		// Delete a cover image
		await groupStore.deleteGroupImage(groupId.value, GROUP_IMAGE_FIELD.COVER_IMAGE);
	}

	if (profileStore.errorMessage) {
		notificationStore.showErrorNotification(profileStore.errorMessage);
	} else if (profileStore.social.errorMessage) {
		notificationStore.showErrorNotification(profileStore.social.errorMessage);
	} else {
		notificationStore.showSuccessNotification('Successfully updated your group.');
		trackEvent({
			event: GTM_EVENT_NAME.EDIT_GROUP_SUCCESS,
			user_id: isOwner.value ? profileId.value : organizerId.value,
			group_id: groupId.value,
		});
	}
	isShowGroupEditDialog.value = false;
	await groupStore.getGroupInfo(groupId.value);
}

async function handleOnFetchFollowerList($state: any) {
	const isComplete = profileStore.follower.data?.paginationInfo.totalItems === followers.value.length;
	if (!groupId.value || isComplete) {
		$state.complete();
		return;
	}

	await profileStore.getFollowerList(
		groupId.value,
		{
			itemPerPage: 8,
			currentPage: currentFollowerListPage.value,
			sortBy: 'desc(pod_user.created)',
		},
	);

	if (!profileStore.follower.data?.data.length) {
		$state.complete();
		return;
	}

	followers.value.push(...profileStore.follower.data.data);
	$state.loaded();
	currentFollowerListPage.value++;

	if (profileStore.follower.errorMessage) {
		notificationStore.showErrorNotification(profileStore.follower.errorMessage);
	}
}

async function handleFollowGroup() {
	await followBtnRef.value.handleFollowGroup();
	isOpenSidebar.value = false;
}

async function handleOnUpdateFollowingStatus() {
	if (groupId.value) {
		await groupStore.getGroupInfo(groupId.value);
	}
	isOpenSidebar.value = false;
}

function handleOnSignIn() {
	isOpenSidebar.value = true;
}

async function checkGroupIdOwnership(userId: number, checkingGroupId: number) {
	if (!authStore.isLoggedIn) {
		return;
	}
	await userStore.getGroupRole(userId, checkingGroupId);
}

function handleScrollToHostList() {
	// Scroll to hosting list if hash contains #hosting-list
	// When event list is loaded
	if (route.hash === '#hosting-list') {
		const element = document.querySelector(route.hash);
		if (element) {
			element.scrollIntoView({ behavior: 'smooth' });
		}
	}
}

function handleGoogleLoginFailed() {
	isShowGoogleLoginFailDialog.value = true;
}

watch(() => userStore.isOwner, (newValue) => {
	if (newValue) {
		isOwner.value = newValue;
	}
});

// Watch for changes in the group data and update group form
watch(() => groupStore.groupInfo, (newData) => {
	if (newData) {
		followerNumber.value = newData.stats.followers;

		group.groupName = newData.name;
		group.groupDescription = newData.description;
		group.groupImageUrl = newData.logo;
		group.groupCoverImageUrl = newData.cover;

		setDocumentTitle(route.name, group.groupName);
	}
});

// Reset infinite loading state when follow status changes
watch(() => followerNumber.value, (newValue) => {
	if (newValue) {
		followers.value = [];
		currentFollowerListPage.value = 1;
	}
});

// Update follower number when trgigered follow/unfollow function
watch(() => userStore.follower.isUpdated, (newValue) => {
	if (newValue) {
		handleOnUpdateFollowingStatus();
	}
});

watch(
	() => profileId.value,
	async (newProfileId) => {
		if (!groupId.value) {
			router.push({
				name: ROUTE.ERROR_404.NAME,
			});
			return;
		}

		if (newProfileId) {
			// When the user is signed in, check group ownership
			await checkGroupIdOwnership(newProfileId, groupId.value);
		} else {
			// If there's no profile or the user isn't logged in, set ownership flag to false
			isOwner.value = false;
		}
		await groupStore.getGroupInfo(groupId.value);
	},
	{ immediate: true },
);

onMounted(async () => {
	trackEvent({
		event: GTM_EVENT_NAME.VIEW_GROUP_SUCCESS,
		user_id: isOwner.value ? profileId.value : organizerId.value,
		group_id: groupId.value,
	});
});
</script>

<template>
  <div class="profile-page-container page">
    <UserProfileHeader
      :name="group.groupName"
      :profile-image-url="group.groupImageUrl"
      :cover-image-url="group.groupCoverImageUrl"
      :is-loading="isLoading"
      is-group-profile
    >
      <template #upperDetail>
        <MultipleSkeletonLoader
          v-if="isLoading"
          class="upper-detail-loader"
          space="12"
          :rows="isOwner ? 4 : 2"
          is-row-direction
        >
          <ButtonSkeletonLoader
            height="38"
            width="38"
          />
        </MultipleSkeletonLoader>
        <div
          v-else
          class="action-container"
        >
          <template v-if="isOwner">
            <RouterLink
              :to="{
                name: ROUTE.EVENT_CREATE.NAME,
                query: { groupId },
              }"
              target="_blank"
            >
              <BaseButton
                class="create-btn"
                variant="solid"
              >
                <template #leftIcon>
                  <PlusIcon />
                </template>
                <p
                  v-if="!isMobile"
                  class="text-sm text-semibold"
                >
                  Create event
                </p>
              </BaseButton>
            </RouterLink>
            <BaseButton
              variant="outlined"
              color="gray"
              size="sm"
              :disabled="isLoading"
              @click="isShowGroupEditDialog = true"
            >
              <template #leftIcon>
                <EditIcon />
              </template>
              <template #tooltip>
                <p>Edit</p>
              </template>
            </BaseButton>
            <BaseButton
              variant="outlined"
              color="gray"
              size="sm"
              :disabled="isLoading"
              @click="handleShowMessageDialog"
            >
              <template #leftIcon>
                <MailIcon />
              </template>
              <template #tooltip>
                <p>Send message</p>
              </template>
            </BaseButton>
          </template>
          <FollowButton
            v-else
            ref="followBtnRef"
            :user-id="profileId"
            :group-id="groupId"
            :role="userRole"
            :is-loading="isLoading"
            :is-logged-in="authStore.isLoggedIn"
            @on-sign-in="handleOnSignIn"
          />
          <ShareButton
            :url="sharedGroupUrl"
            :user-id="profileId"
            :group-id="groupId"
            :gtm-event-name="GTM_EVENT_NAME.SHARE_GROUP_SUCCESS"
            show-only-icon
          />
        </div>
      </template>
      <template #lowerDetail>
        <GroupProfileInfoSkeletonLoader
          v-if="isLoading"
          :action-count="isOwner ? 1 : 2"
        />
        <template v-else>
          <div class="link-container">
            <AvatarLink
              v-if="!isOwner"
              class="avatar-link"
              :image-url="organizer?.avatarLarge"
              :alt="organizer?.nickname"
              :size="AvatarSizes[0]"
              :user-id="organizerId"
            >
              <template #prefix>
                <p class="text-md">
                  By
                </p>
              </template>
              <template #content>
                <p class="organizer-name text-md">
                  {{ organizer?.nickname || '' }}
                </p>
              </template>
            </AvatarLink>
            <FollowerList
              :follower-number="followerNumber"
              :followers="followers"
              @on-fetch-data="handleOnFetchFollowerList"
            />
          </div>
          <p
            v-if="group.groupDescription"
            class="text-md text-regular text-left"
          >
            {{ group.groupDescription }}
          </p>
        </template>
      </template>
    </UserProfileHeader>

    <Divider />

    <div class="event-container">
      <MyAccountHosting
        :user-id="isOwner ? profileId : organizerId"
        :group-id="groupId"
        :is-owner="userStore.isOwner"
        :is-loading="isLoading"
        @on-update="handleScrollToHostList"
      />
    </div>

    <SendMessageDialog
      receiver="All followers"
      :is-show="isShowMessageDialog"
      :disabled="isLoading"
      @on-close="handleOnDialogClose"
      @on-send="handleOnSendMessage"
    />
    <ConfirmDialog
      :is-show="isShowNoFollowerDialog"
      confirm-button-name="Ok, I got it"
      hide-cancel-button
      @on-close="isShowNoFollowerDialog = false"
      @on-confirm="isShowNoFollowerDialog = false"
    >
      <template #header>
        <InfoCircleIcon
          class="icon-header-confirm-dlg icon-info"
          :color="ICON_COLOR.PRIMARY"
        />
      </template>
      <template #title>
        No followers at the moment
      </template>
      <template #content>
        <p class="text-sm text-regular">
          You currently do not have any followers to send your message to.
        </p>
      </template>
    </ConfirmDialog>
    <GroupDialog
      :is-show="isShowGroupEditDialog"
      :disabled="profileStore.isLoading"
      @on-close="isShowGroupEditDialog = false"
      @on-update="handleUpdateGroup"
    />
    <AuthenticationSideDrawerForm
      :is-open="isOpenSidebar"
      :is-loading="authStore.isLoading"
      :redirect-ids="{ groupId }"
      :page="AUTH_PAGE_ORIGIN.GROUP"
      @on-close="handleSideDrawerClose"
      @on-cancel="handleSideDrawerClose"
      @on-success="handleFollowGroup"
      @on-google-login-failed="handleGoogleLoginFailed"
    />
    <GoogleLoginFailByInAppBrowserDialog
      :is-show="isShowGoogleLoginFailDialog"
      @on-close="isShowGoogleLoginFailDialog = false"
      @on-confirm="isShowGoogleLoginFailDialog = false"
    />
  </div>
</template>

<style lang="scss" scoped>
.profile-page-container {
	margin: auto;
	padding-top: 0;

	:deep(.profile-header-wrapper) {
		padding: 0 0 rem(48);
	}

	.link-container {
		display: flex;
		gap: spacings-get(6);

		.event-organizer {
			display: flex;
			gap: spacings-get(1);
			color: colors-get(gray, 500);
		}
	}

	.upper-detail-loader {
		width: rem(138);
		margin-top: rem(62);
		display: flex;
		flex-direction: row-reverse;

		:deep(.skeleton-item) {
			width: rem(38);
		}
	}

	.action-container {
		display: flex;
		gap: spacings-get(3);
		margin-top: rem(62);

		button {
			padding: spacings-get(2);
		}

		.create-btn {
			padding: spacings-get(2) rem(14);

			@include media-query-max(mobile) {
				padding: spacings-get(2);
			}
		}
	}

	.event-container {
		@include media-query-max(mobile) {
			padding: 0 spacings-get(4);
		}
	}
}
</style>